import { useCallback, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  BookOpen,
  ChevronRight,
  ClipboardCopy,
  Clock,
  Copy,
  Download,
  Eye,
  FileText,
  MessageSquare,
  Pencil,
  Plus,
  Sparkles,
  RefreshCw,
  Loader2,
  Trash2,
  Upload,
  X,
} from 'lucide-react'
import { TopBar } from '../components/TopBar'
import { MessageBubble } from '../components/MessageBubble'
import { Composer } from '../components/Composer'
import type { ComposerHandle } from '../components/Composer'
import { useI18n } from '../lib/i18n'
import { useChat, type ChatSettings } from '../hooks/useChat'
import { exportChatToDocx, exportDocToDocx } from '../lib/exportChat'
import { AIConfirmModal } from '../components/AIConfirmModal'
import {
  logAIContentConfirmation,
  fetchDocumentTypes,
  fetchMatterDocuments,
  generateMatterDocument,
  deleteMatterDocument,
  type DocTypeItem,
  type MatterDocument,
} from '../lib/api'
import type { ChatTurn, ConversationDetail, ConversationSummary, KnowledgeCategory, Matter } from '../lib/types'
import { SnippetProvider } from '../contexts/SnippetContext'
import { CASE_TYPES, COURT_LOCATIONS, translateCaseType, translateCourtLocation } from '../lib/caseConstants'

// ─── i18n ────────────────────────────────────────────────────────────────────
const LABELS = {
  zh: {
    pageTitle: '工作台',
    backToList: '返回列表',
    statusPlanning: '规划中',
    statusActive: '进行中',
    statusClosed: '已完成',
    createdAt: '创建于',
    exportCase: '导出案件',
    addMaterial: '添加材料',
    deleteCase: '删除案件',
    deleteConfirm: '确定要删除此案件吗？此操作不可恢复。',
    // 左侧
    caseInfo: '案件信息',
    caseName: '案件名称',
    caseNumber: '案件编号',
    caseType: '案件类型',
    courtName: '法院名称',
    courtLocation: '法院所在地',
    jurisdiction: '职权范围',
    caseSummary: '案件简介',
    linkedKb: '挂载知识库',
    manage: '管理',
    caseMaterials: '案件材料',
    filesCount: '个文件',
    // tabs
    tabChat: 'AI 对话',
    tabDocs: '文书生成',
    tabMaterials: '材料管理',
    // AI 对话
    inputPlaceholder: '输入您的问题...',
    newConversation: '新对话',
    historyConversations: '历史对话',
    noConversations: '暂无对话记录',
    deleteConv: '删除对话',
    deleteConvConfirm: '确定要删除这条对话吗？',
    turnsCount: '轮对话',
    // 文书生成
    docGenTitle: '生成法律文书草稿',
    docGenDesc: '基于案件材料、AI 分析结果及知识库，生成符合马来西亚法庭习惯的文书草稿',
    selectDocType: '选择文书类型',
    generatedDocs: '已生成的文书',
    generatedAt: '生成于',
    docTypeTitle: '选择文书类型',
    docTypeDesc: '请选择需要生成的文书类型',
    docSelectFiles: '选择案件材料',
    docSelectFilesDesc: '勾选用于生成文书的案件材料',
    docSelectAll: '全选',
    docNoFiles: '该案件暂无已上传的材料',
    docFilesSelected: '个文件',
    docInstructions: '额外要求（可选）',
    docInstructionsPlaceholder: '例如：重点关注财产分割部分…',
    generating: '正在生成文书…',
    generate: '生成',
    docCancel: '取消',
    docConfirm: '确认',
    copySuccess: '已复制到剪贴板',
    copyFailed: '复制失败',
    exportWord: '导出为Word',
    deleteDoc: '删除文书',
    deleteDocConfirm: '确定要删除这份文书吗？',
    docEmpty: '暂无已生成的文书，请选择文书类型开始生成',
    docGenerateFailed: '文书生成失败',
    docStatusGenerating: '生成中…',
    docStatusFailed: '生成失败',
    viewFullDoc: '查看全文',
    closeDoc: '关闭',
    sourcesCount: '条参考来源',
    // 材料管理
    materialsTitle: '案件材料',
    uploadMaterial: '上传材料',
    certificate: '证件',
    propertyDoc: '产权文件',
    evidence: '证据',
    caseDesc: '案件说明',
    providedByClient: '由客户提供',
    materialsPendingUploading: '上传中…',
    materialsPendingProcessing: '解析中…',
    materialsUploadFailed: '上传失败',
    materialsOcrFailed: '解析失败',
    clientName: '客户名称',
    opposingParty: '对方当事人',
    editCase: '编辑案件信息',
    save: '保存',
    cancel: '取消',
    customType: '自定义案件类型',
    customTypePlaceholder: '请输入案件类型',
    customLocation: '自定义法院所在地',
    customLocationPlaceholder: '请输入法院所在地',
    recentUsed: '最近使用：',
  },
  en: {
    pageTitle: 'Dashboard',
    backToList: 'Back to list',
    statusPlanning: 'Planning',
    statusActive: 'Active',
    statusClosed: 'Closed',
    createdAt: 'Created',
    exportCase: 'Export Case',
    addMaterial: 'Add Material',
    deleteCase: 'Delete Case',
    deleteConfirm: 'Are you sure you want to delete this case? This action cannot be undone.',
    caseInfo: 'Case Information',
    caseName: 'Case Name',
    caseNumber: 'Case Number',
    caseType: 'Case Type',
    courtName: 'Court Name',
    courtLocation: 'Court Location',
    jurisdiction: 'Jurisdiction',
    caseSummary: 'Case Summary',
    linkedKb: 'Linked Knowledge Base',
    manage: 'Manage',
    caseMaterials: 'Case Materials',
    filesCount: 'files',
    tabChat: 'AI Chat',
    tabDocs: 'Doc Generation',
    tabMaterials: 'Materials',
    inputPlaceholder: 'Enter your question...',
    newConversation: 'New Chat',
    historyConversations: 'History',
    noConversations: 'No conversations yet',
    deleteConv: 'Delete',
    deleteConvConfirm: 'Delete this conversation?',
    turnsCount: 'turns',
    docGenTitle: 'Generate Legal Document Drafts',
    docGenDesc: 'Generate drafts based on case materials, AI analysis and knowledge base, following Malaysian court standards',
    selectDocType: 'Select Document Type',
    generatedDocs: 'Generated Documents',
    generatedAt: 'Generated at',
    docTypeTitle: 'Select Document Type',
    docTypeDesc: 'Choose the type of document to generate',
    docSelectFiles: 'Select Case Materials',
    docSelectFilesDesc: 'Choose which case files to use for generation',
    docSelectAll: 'Select All',
    docNoFiles: 'No uploaded files for this case',
    docFilesSelected: 'files',
    docInstructions: 'Additional instructions (optional)',
    docInstructionsPlaceholder: 'e.g. Focus on property division…',
    generating: 'Generating document…',
    generate: 'Generate',
    docCancel: 'Cancel',
    docConfirm: 'Confirm',
    copySuccess: 'Copied to clipboard',
    copyFailed: 'Copy failed',
    exportWord: 'Export to Word',
    deleteDoc: 'Delete Document',
    deleteDocConfirm: 'Are you sure you want to delete this document?',
    docEmpty: 'No documents yet. Select a document type to start generating.',
    docGenerateFailed: 'Document generation failed',
    docStatusGenerating: 'Generating…',
    docStatusFailed: 'Failed',
    viewFullDoc: 'View Full Document',
    closeDoc: 'Close',
    sourcesCount: 'sources',
    materialsTitle: 'Case Materials',
    uploadMaterial: 'Upload Material',
    certificate: 'Certificate',
    propertyDoc: 'Property Doc',
    evidence: 'Evidence',
    caseDesc: 'Case Description',
    providedByClient: 'Provided by client',
    materialsPendingUploading: 'Uploading document…',
    materialsPendingProcessing: 'Parsing document…',
    materialsUploadFailed: 'Upload failed',
    materialsOcrFailed: 'Parsing failed',
    clientName: 'Client Name',
    opposingParty: 'Opposing Party',
    editCase: 'Edit Case Info',
    save: 'Save',
    cancel: 'Cancel',
    customType: 'Custom Case Type',
    customTypePlaceholder: 'Enter case type',
    customLocation: 'Custom Court Location',
    customLocationPlaceholder: 'Enter court location',
    recentUsed: 'Recent:',
  },
}

type PendingMatterUploadStatus = 'uploading' | 'processing' | 'error'

interface PendingMatterUpload {
  id: string
  filename: string
  size_bytes: number
  status: PendingMatterUploadStatus
  fileId?: string
  error?: string
}

type Tab = 'chat' | 'docs' | 'materials'

// ─── Mock data removed — DocsTab now uses real API ───────────────────────────

const DEFAULT_CHAT_SETTINGS: ChatSettings = {
  mode: 'hybrid',
  top_k: 8,
  phrase: false,
  temperature: 0.1,
  categoryPresets: [],
}

// ─── 主组件 ──────────────────────────────────────────────────────────────────
export function MatterDetail() {
  const { lang, docLang } = useI18n()
  const t = LABELS[lang] ?? LABELS.zh
  const navigate = useNavigate()
  const { matterId } = useParams<{ matterId: string }>()

  const [matter, setMatter] = useState<Matter | null>(null)
  const [activeTab, setActiveTab] = useState<Tab>('chat')

  // ─── 挂载知识库 ──────────────────────────────────────────────────
  const [linkedKbKeys, setLinkedKbKeys] = useState<string[]>([])
  const [allCategories, setAllCategories] = useState<KnowledgeCategory[]>([])
  const [showKbManage, setShowKbManage] = useState(false)

  const loadLinkedKb = useCallback(() => {
    if (!matterId) return
    fetch(`/matters/${matterId}/knowledge-links`)
      .then((r) => r.json())
      .then((data: { category_key: string }[]) => {
        if (Array.isArray(data)) setLinkedKbKeys(data.map((d) => d.category_key))
      })
      .catch(() => {})
  }, [matterId])

  useEffect(() => { loadLinkedKb() }, [loadLinkedKb])

  useEffect(() => {
    fetch('/knowledge/categories')
      .then((r) => r.json())
      .then((data: { categories?: KnowledgeCategory[] }) => {
        if (data.categories) setAllCategories(data.categories)
      })
      .catch(() => {})
  }, [])

  // ─── 案件材料（用于左侧栏）─────────────────────────────────────────────
  const [matterFiles, setMatterFiles] = useState<MatterFileItem[]>([])
  const addMaterialRef = useRef<HTMLInputElement>(null)
  const [pendingMaterialUploads, setPendingMaterialUploads] = useState<PendingMatterUpload[]>([])
  const [isUploadingMaterials, setIsUploadingMaterials] = useState(false)

  const loadMatterFiles = useCallback(() => {
    if (!matterId) return
    fetch(`/matters/${matterId}/files`)
      .then((r) => r.json())
      .then((data: { items?: MatterFileItem[] }) => {
        if (data.items) setMatterFiles(data.items)
      })
      .catch(() => {})
  }, [matterId])

  const uploadMatterFiles = useCallback(
    async (fileList: FileList | null) => {
      if (!matterId || !fileList || fileList.length === 0) return
      setIsUploadingMaterials(true)
      const filesArray = Array.from(fileList)
      const newUploads: PendingMatterUpload[] = filesArray.map((file) => ({
        id: `${file.name}-${Date.now()}-${Math.random().toString(16).slice(2)}`,
        filename: file.name,
        size_bytes: file.size,
        status: 'uploading',
      }))
      setPendingMaterialUploads((prev) => [...prev, ...newUploads])
      try {
        for (let i = 0; i < filesArray.length; i++) {
          const file = filesArray[i]
          const pending = newUploads[i]
          const formData = new FormData()
          formData.append('file', file)
          const res = await fetch(`/matters/${matterId}/files?category=other`, {
            method: 'POST',
            body: formData,
          })
          if (!res.ok) {
            const body = await res.text().catch(() => '')
            const message = body || `HTTP ${res.status}`
            throw new Error(message)
          }
          const data: MatterFileItem = await res.json()
          setPendingMaterialUploads((prev) =>
            prev.map((p) =>
              p.id === pending.id
                ? { ...p, status: 'processing', fileId: data.file_id }
                : p,
            ),
          )
          loadMatterFiles()
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Upload failed'
        setPendingMaterialUploads((prev) =>
          prev.map((p) =>
            p.status === 'uploading' ? { ...p, status: 'error', error: message } : p,
          ),
        )
      } finally {
        setIsUploadingMaterials(false)
      }
    },
    [matterId, loadMatterFiles],
  )

  useEffect(() => {
    loadMatterFiles()
    // 轮询刷新处理状态（OCR/文本提取在后台运行）
    const interval = setInterval(() => {
      console.log('[poll] refreshing matter files', matterId)
      loadMatterFiles()
    }, 2000)
    return () => clearInterval(interval)
  }, [loadMatterFiles, matterId])

  // 文件解析完成后，自动从 pending uploads 列表中移除
  useEffect(() => {
    setPendingMaterialUploads((prev) =>
      prev.filter((p) => {
        if (!p.fileId) return true
        const found = matterFiles.find((f) => f.file_id === p.fileId)
        return !found || found.processing_status !== 'done'
      }),
    )
  }, [matterFiles])

  // ─── 文件预览 ───────────────────────────────────────────────────────────
  const [previewFile, setPreviewFile] = useState<MatterFileItem | null>(null)

  // ─── 左侧栏宽度拖拽 ─────────────────────────────────────────────────────
  const [sidebarWidth, setSidebarWidth] = useState(224)
  const dragRef = useRef<{ startX: number; startW: number } | null>(null)

  const handleSidebarDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    dragRef.current = { startX: e.clientX, startW: sidebarWidth }
    const onMove = (ev: MouseEvent) => {
      if (!dragRef.current) return
      const next = Math.max(160, dragRef.current.startW + ev.clientX - dragRef.current.startX)
      setSidebarWidth(next)
    }
    const onUp = () => {
      dragRef.current = null
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [sidebarWidth])

  // ─── 对话历史管理 ─────────────────────────────────────────────────────
  const [conversations, setConversations] = useState<ConversationSummary[]>([])
  const [activeConvId, setActiveConvId] = useState<string | null>(null)
  const [loadedTurns, setLoadedTurns] = useState<ChatTurn[]>([])

  // AI 对话
  const chatHook = useChat()
  const [chatSettings] = useState<ChatSettings>(DEFAULT_CHAT_SETTINGS)
  const prevHistoryLenRef = useRef(0)
  // useChat 只有一个流式状态；这里记录它属于哪个会话。
  // 这样切换历史会话时只切换视图，不会清掉/中断后台仍在生成的回答。
  const [localConvId, setLocalConvId] = useState<string | null>(null)
  const [localBaseTurns, setLocalBaseTurns] = useState<ChatTurn[]>([])
  const [, setStreamConvId] = useState<string | null>(null)
  const generationConvIdRef = useRef<string | null>(null)

  // 加载案件数据
  useEffect(() => {
    if (!matterId) return
    fetch(`/matters/${matterId}`)
      .then((r) => r.json())
      .then((data: Matter) => setMatter(data))
      .catch(() => {
        fetch('/matters?limit=50')
          .then((r) => r.json())
          .then((data: { items?: Matter[] }) => {
            const found = data.items?.find((m) => m.matter_id === matterId)
            if (found) setMatter(found)
          })
          .catch(() => {})
      })
  }, [matterId])

  // 加载对话列表
  const loadConversations = useCallback(() => {
    if (!matterId) return
    fetch(`/matters/${matterId}/conversations`)
      .then((r) => r.json())
      .then((data: { items?: ConversationSummary[] }) => {
        if (data.items) setConversations(data.items)
      })
      .catch(() => {})
  }, [matterId])

  const updateConversationSummary = useCallback((detail: ConversationDetail) => {
    const summary: ConversationSummary = {
      conv_id: detail.conv_id,
      matter_id: detail.matter_id,
      title: detail.title,
      turn_count: detail.turn_count,
      is_ai_generated: detail.is_ai_generated,
      created_at: detail.created_at,
      updated_at: detail.updated_at,
    }
    setConversations((prev) => {
      const rest = prev.filter((conv) => conv.conv_id !== summary.conv_id)
      return [summary, ...rest]
    })
  }, [])

  useEffect(() => {
    loadConversations()
  }, [loadConversations])

  // 选中对话时加载完整历史；不清空 chatHook，避免中断后台仍在生成的回答
  const selectConversation = useCallback((convId: string) => {
    if (!matterId) return
    setActiveConvId(convId)
    fetch(`/matters/${matterId}/conversations/${convId}`)
      .then((r) => r.json())
      .then((data: ConversationDetail) => {
        const turns: ChatTurn[] = data.turns.map((t) => ({
          role: t.role as 'user' | 'assistant',
          content: t.content,
          reasoning: t.reasoning ?? undefined,
          steps: t.steps ?? undefined,
          sources: t.sources ?? undefined,
          stats: t.stats ?? undefined,
          verification: t.verification ?? undefined,
        }))
        setLoadedTurns(turns)
      })
      .catch(() => {})
  }, [matterId])

  // 新建对话。若当前正在生成，只切换到一个新的空白会话视图，不中断生成。
  const startNewConversation = useCallback(() => {
    setActiveConvId(null)
    setLoadedTurns([])
    if (!chatHook.isStreaming) {
      chatHook.clear()
      setLocalConvId(null)
      setLocalBaseTurns([])
      setStreamConvId(null)
      generationConvIdRef.current = null
    }
  }, [chatHook])

  // 自动保存：回答完成后只追加助手回答；用户问题已在发送瞬间写入。
  useEffect(() => {
    const history = chatHook.history
    const prevLen = prevHistoryLenRef.current
    prevHistoryLenRef.current = history.length

    if (history.length <= prevLen || history.length < 2) return
    if (!matterId) return

    const newTurns = history.slice(prevLen)
    if (newTurns.length === 0) return

    const targetConvId = generationConvIdRef.current
    const assistantTurns = newTurns.filter((t) => t.role === 'assistant')
    if (assistantTurns.length === 0) return

    const turnsPayload = assistantTurns.map((t) => ({
      role: t.role,
      content: t.content,
      reasoning: t.reasoning ?? null,
      steps: t.steps ?? null,
      sources: t.sources ?? null,
      stats: t.stats ?? null,
      verification: t.verification ?? null,
    }))

    if (targetConvId) {
      fetch(`/matters/${matterId}/conversations/${targetConvId}/turns`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ turns: turnsPayload }),
      })
        .then((r) => r.json())
        .then((conv: ConversationDetail) => {
          const savedAssistantTurns: ChatTurn[] = conv.turns
            .slice(-assistantTurns.length)
            .map((turn) => ({
              role: turn.role as 'user' | 'assistant',
              content: turn.content,
              reasoning: turn.reasoning ?? undefined,
              steps: turn.steps ?? undefined,
              sources: turn.sources ?? undefined,
              stats: turn.stats ?? undefined,
              verification: turn.verification ?? undefined,
            }))
          if (activeConvId === targetConvId) {
            setLoadedTurns((prev) => [...prev, ...savedAssistantTurns])
          }
          setLocalBaseTurns((prev) => [...prev, ...savedAssistantTurns])
          updateConversationSummary(conv)
          chatHook.setHistory([])
        })
        .catch(() => {})
    } else {
      // 兜底：理论上发送瞬间已经创建会话；只有发送前落库失败才会走到这里。
      const firstQuestion = newTurns.find((t) => t.role === 'user')?.content ?? ''
      fetch(`/matters/${matterId}/conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: firstQuestion.slice(0, 50) }),
      })
        .then((r) => r.json())
        .then((conv: ConversationDetail) => {
          setLocalConvId(conv.conv_id)
          setStreamConvId(conv.conv_id)
          generationConvIdRef.current = conv.conv_id
          if (activeConvId === null) setActiveConvId(conv.conv_id)
          return fetch(`/matters/${matterId}/conversations/${conv.conv_id}/turns`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ turns: turnsPayload }),
          })
        })
        .then((r) => r.json())
        .then((conv: ConversationDetail) => {
          updateConversationSummary(conv)
          chatHook.setHistory([])
        })
        .catch(() => {})
    }
  }, [chatHook.history, chatHook.setHistory, matterId, activeConvId, updateConversationSummary])

  if (!matter) {
    return (
      <>
        <TopBar title={t.pageTitle} />
        <div className="flex-1 flex items-center justify-center text-brand-400 text-sm">
          Loading...
        </div>
      </>
    )
  }

  const STATUS_OPTIONS: { value: Matter['status']; label: string; color: string }[] = [
    { value: 'planning', label: t.statusPlanning, color: 'bg-amber-100 text-amber-700' },
    { value: 'active', label: t.statusActive, color: 'bg-blue-100 text-blue-700' },
    { value: 'closed', label: t.statusClosed, color: 'bg-green-100 text-green-700' },
  ]

  const currentStatus = STATUS_OPTIONS.find((s) => s.value === matter.status) ?? STATUS_OPTIONS[0]

  const handleStatusChange = (newStatus: Matter['status']) => {
    if (!matterId || newStatus === matter.status) return
    fetch(`/matters/${matterId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status: newStatus }),
    })
      .then((r) => r.json())
      .then((updated: Matter) => setMatter(updated))
      .catch(() => {})
  }

  const handleDelete = () => {
    if (!matterId || !window.confirm(t.deleteConfirm)) return
    fetch(`/matters/${matterId}`, { method: 'DELETE' })
      .then((r) => {
        if (r.ok) navigate('/matters')
      })
      .catch(() => {})
  }

  return (
    <>
      <TopBar title={t.pageTitle} />
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* 顶部案件标题栏 */}
        <div className="flex items-center gap-4 px-6 py-4 border-b border-brand-200 bg-white flex-shrink-0">
          <button
            type="button"
            onClick={() => navigate('/matters')}
            className="p-1.5 rounded-lg text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-brand-800 truncate">
                {matter.title}
              </h2>
              {/* 状态切换下拉 */}
              <div className="relative group/status">
                <button
                  type="button"
                  className={`px-2.5 py-0.5 rounded-full text-[10px] font-medium flex-shrink-0 cursor-pointer ${currentStatus.color}`}
                >
                  {currentStatus.label}
                </button>
                <div className="absolute top-full left-0 mt-1 bg-white border border-brand-200 rounded-lg shadow-lg py-1 min-w-[100px] z-10 hidden group-hover/status:block">
                  {STATUS_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => handleStatusChange(opt.value)}
                      className={`w-full text-left px-3 py-1.5 text-xs hover:bg-brand-50 transition-colors ${
                        opt.value === matter.status ? 'font-semibold text-brand-800' : 'text-brand-600'
                      }`}
                    >
                      <span className={`inline-block w-2 h-2 rounded-full mr-2 ${opt.color.split(' ')[0]}`} />
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="text-xs text-brand-400 mt-0.5">
              {matter.case_number} · {t.createdAt} {matter.opened_at}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={handleDelete}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 text-sm text-red-500 hover:bg-red-50 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              {t.deleteCase}
            </button>
            <button
              type="button"
              onClick={() => {
                const info = [
                  `${t.caseName}: ${matter.title}`,
                  `${t.caseNumber}: ${matter.case_number ?? '-'}`,
                  `${t.caseType}: ${translateCaseType(matter.practice_area, lang)}`,
                  `${t.caseSummary}: ${matter.summary ?? '-'}`,
                  `${t.createdAt}: ${matter.opened_at}`,
                  '',
                  `${t.caseMaterials} (${matterFiles.length}):`,
                  ...matterFiles.map((f) => `  - ${f.filename} (${f.category})`),
                ].join('\n')
                const blob = new Blob([info], { type: 'text/plain;charset=utf-8' })
                const url = URL.createObjectURL(blob)
                const a = document.createElement('a')
                a.href = url
                a.download = `${matter.case_number ?? matter.title}.txt`
                a.click()
                URL.revokeObjectURL(url)
              }}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-brand-300 text-sm text-brand-600 hover:bg-brand-50 transition-colors"
            >
              <ClipboardCopy className="w-4 h-4" />
              {t.exportCase}
            </button>
            <button
              type="button"
              onClick={() => addMaterialRef.current?.click()}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              {t.addMaterial}
            </button>
            <input
              ref={addMaterialRef}
              type="file"
              multiple
              className="hidden"
              onChange={async (e) => {
                const fileList = e.target.files
                if (!fileList || fileList.length === 0) return
                for (const file of Array.from(fileList)) {
                  const formData = new FormData()
                  formData.append('file', file)
                  try {
                    await fetch(`/matters/${matterId}/files?category=other`, {
                      method: 'POST',
                      body: formData,
                    })
                  } catch { /* ignore */ }
                }
                loadMatterFiles()
                e.target.value = ''
              }}
            />
          </div>
        </div>

        {/* 主内容区域：左侧信息栏 + 中间内容 */}
        <div className="flex flex-1 min-h-0">
          {/* 左侧信息栏 — 宽度可拖拽 */}
          <div style={{ width: sidebarWidth }} className="flex-shrink-0 flex flex-col min-h-0">
            <LeftSidebar
              matter={matter}
              lang={lang}
              t={t}
              matterFiles={matterFiles}
              onPreview={setPreviewFile}
              linkedKbKeys={linkedKbKeys}
              allCategories={allCategories}
              showKbManage={showKbManage}
              onShowKbManage={setShowKbManage}
              matterId={matterId}
              onLinkedKbChange={loadLinkedKb}
              onMatterUpdated={setMatter}
            />
          </div>

          {/* 拖拽分隔条 */}
          <div
            onMouseDown={handleSidebarDragStart}
            className="w-1 flex-shrink-0 cursor-col-resize bg-brand-200 hover:bg-blue-400 transition-colors select-none"
          />

          {/* 中间内容区 */}
          <div className="flex-1 flex flex-col min-w-0">
            {/* Tab 导航 */}
            <div className="flex items-center gap-6 px-6 pt-4 border-b border-brand-200 bg-white">
              {(['chat', 'docs', 'materials'] as Tab[]).map((tab) => {
                const icon = tab === 'chat' ? MessageSquare
                  : tab === 'docs' ? FileText
                  : Upload
                const label = tab === 'chat' ? t.tabChat
                  : tab === 'docs' ? t.tabDocs
                  : t.tabMaterials
                const Icon = icon
                const isActive = activeTab === tab
                return (
                  <button
                    key={tab}
                    type="button"
                    onClick={() => setActiveTab(tab)}
                    className={`flex items-center gap-1.5 pb-3 text-sm font-medium border-b-2 transition-colors ${
                      isActive
                        ? 'border-blue-600 text-blue-700'
                        : 'border-transparent text-brand-500 hover:text-brand-700'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                  </button>
                )
              })}
            </div>

            {/* Tab 内容 */}
            <div className="flex-1 flex flex-col min-h-0 bg-brand-50">
              {activeTab === 'chat' && (
                <SnippetProvider>
                  <ChatTab
                    chatHook={chatHook}
                    chatSettings={chatSettings}
                    conversations={conversations}
                    activeConvId={activeConvId}
                    loadedTurns={loadedTurns}
                    localConvId={localConvId}
                    localBaseTurns={localBaseTurns}
                    setActiveConvId={setActiveConvId}
                    setLocalConvId={setLocalConvId}
                    setLocalBaseTurns={setLocalBaseTurns}
                    setStreamConvId={setStreamConvId}
                    generationConvIdRef={generationConvIdRef}
                    onSelectConv={selectConversation}
                    onNewConv={startNewConversation}
                    onDeleteConv={(convId) => {
                      if (!matterId) return
                      fetch(`/matters/${matterId}/conversations/${convId}`, { method: 'DELETE' })
                        .then((r) => {
                          if (r.ok) {
                            loadConversations()
                            if (activeConvId === convId) startNewConversation()
                          }
                        })
                        .catch(() => {})
                    }}
                    matterId={matterId}
                    setLoadedTurns={setLoadedTurns}
                    loadConversations={loadConversations}
                    updateConversationSummary={updateConversationSummary}
                    t={t}
                  />
                </SnippetProvider>
              )}
              {activeTab === 'docs' && <DocsTab matterId={matterId!} lang={lang} docLang={docLang} t={t} />}
              {activeTab === 'materials' && (
                <MaterialsTab
                  matterId={matterId!}
                  lang={lang}
                  t={t}
                  onFilesChange={loadMatterFiles}
                  onPreview={setPreviewFile}
                  pendingUploads={pendingMaterialUploads}
                  uploading={isUploadingMaterials}
                  onUploadFiles={uploadMatterFiles}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 文件预览弹窗 */}
      {previewFile && matterId && (
        <FilePreviewModal
          matterId={matterId}
          file={previewFile}
          lang={lang}
          onClose={() => setPreviewFile(null)}
        />
      )}
    </>
  )
}

// ─── 左侧信息栏 ──────────────────────────────────────────────────────────────
function LeftSidebar({
  matter,
  lang,
  t,
  matterFiles,
  onPreview,
  linkedKbKeys,
  allCategories,
  showKbManage,
  onShowKbManage,
  matterId,
  onLinkedKbChange,
  onMatterUpdated,
}: {
  matter: Matter
  lang: string
  t: (typeof LABELS)['zh']
  matterFiles: MatterFileItem[]
  onPreview: (file: MatterFileItem) => void
  linkedKbKeys: string[]
  allCategories: KnowledgeCategory[]
  showKbManage: boolean
  onShowKbManage: (show: boolean) => void
  matterId?: string
  onLinkedKbChange: () => void
  onMatterUpdated: (m: Matter) => void
}) {
  const [showEdit, setShowEdit] = useState(false)

  const getCategoryLabel = (cat: string) => {
    const labels = CATEGORY_LABELS[cat]
    if (!labels) return cat
    return lang === 'zh' ? labels.zh : labels.en
  }

  return (
    <div className="w-full border-r border-brand-200 bg-white overflow-y-auto flex-shrink-0 h-full">
      {/* 案件信息 */}
      <div className="p-4 border-b border-brand-100">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-brand-800">{t.caseInfo}</h4>
          <button
            type="button"
            onClick={() => setShowEdit(true)}
            className="p-1 rounded-lg text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors"
            title={t.editCase}
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="space-y-2.5">
          <InfoField label={t.caseName} value={matter.title} bold />
          <InfoField label={t.caseNumber} value={matter.case_number ?? '-'} mono />
          <InfoField label={t.caseType} value={translateCaseType(matter.practice_area, lang as 'zh' | 'en')} />
          <InfoField label={t.courtName} value={matter.court ?? '-'} />
          <InfoField label={t.courtLocation} value={translateCourtLocation(matter.court_location, lang as 'zh' | 'en')} />
          <InfoField label={t.jurisdiction} value={matter.jurisdiction ?? '-'} />
          <InfoField label={t.clientName} value={matter.client_name ?? '-'} />
          <InfoField label={t.opposingParty} value={matter.opposing_party ?? '-'} />
          <div>
            <div className="text-[11px] text-brand-400 mb-0.5">{t.caseSummary}</div>
            <p className="text-xs text-brand-600 leading-relaxed">
              {matter.summary ?? '-'}
            </p>
          </div>
        </div>
      </div>

      {/* 挂载知识库 */}
      <div className="p-4 border-b border-brand-100">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-brand-800">{t.linkedKb}</h4>
          <button
            type="button"
            onClick={() => onShowKbManage(true)}
            className="text-xs text-blue-600 hover:text-blue-700 font-medium"
          >
            {t.manage}
          </button>
        </div>
        <div className="space-y-1.5">
          {linkedKbKeys.length === 0 ? (
            <div className="text-xs text-brand-400 text-center py-3">
              {lang === 'zh' ? '暂未挂载知识库' : 'No linked knowledge base'}
            </div>
          ) : (
            linkedKbKeys.map((key) => {
              const cat = allCategories.find((c) => c.key === key)
              const label = cat
                ? lang === 'zh' ? cat.name_zh : cat.name_en
                : key
              return (
                <Link
                  key={key}
                  to={`/knowledge?cat=${key}`}
                  className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg hover:bg-brand-50 transition-colors group"
                >
                  <div className="w-7 h-7 rounded-md bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-3.5 h-3.5 text-blue-600" />
                  </div>
                  <span className="text-xs font-medium text-brand-700 flex-1 truncate">
                    {label}
                  </span>
                  <ChevronRight className="w-3.5 h-3.5 text-brand-300 group-hover:text-brand-500 transition-colors" />
                </Link>
              )
            })
          )}
        </div>
      </div>

      {/* 管理挂载知识库弹窗 */}
      {showKbManage && (
        <KbManageModal
          lang={lang}
          allCategories={allCategories}
          linkedKeys={linkedKbKeys}
          onSave={(keys) => {
            if (!matterId) return
            fetch(`/matters/${matterId}/knowledge-links`, {
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ category_keys: keys }),
            })
              .then((r) => { if (r.ok) { onLinkedKbChange(); onShowKbManage(false) } })
              .catch(() => {})
          }}
          onClose={() => onShowKbManage(false)}
        />
      )}

      {/* 案件材料 */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-brand-800">{t.caseMaterials}</h4>
          <span className="text-xs text-brand-400">{matterFiles.length} {t.filesCount}</span>
        </div>
        {matterFiles.length === 0 ? (
          <div className="text-xs text-brand-400 text-center py-4">
            {lang === 'zh' ? '暂无材料' : 'No materials'}
          </div>
        ) : (
          <div className="space-y-1.5">
            {matterFiles.map((file) => (
              <div
                key={file.file_id}
                onClick={() => onPreview(file)}
                className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-brand-50 transition-colors cursor-pointer"
              >
                <FileText className={`w-4 h-4 flex-shrink-0 ${
                  file.filename.endsWith('.pdf') ? 'text-red-400'
                  : file.filename.endsWith('.docx') || file.filename.endsWith('.doc') ? 'text-blue-400'
                  : file.filename.endsWith('.jpg') || file.filename.endsWith('.png') || file.filename.endsWith('.jpeg') ? 'text-green-400'
                  : file.filename.endsWith('.xlsx') || file.filename.endsWith('.xls') ? 'text-emerald-500'
                  : 'text-brand-400'
                }`} />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-brand-700 truncate">{file.filename}</div>
                  <div className="text-[10px] text-brand-400 flex items-center gap-1">
                    {getCategoryLabel(file.category)}
                    {file.processing_status !== 'done' && (
                      <span>
                        · {file.processing_status === 'failed'
                          ? (lang === 'zh' ? '解析失败' : 'Failed')
                          : (lang === 'zh' ? '解析中' : 'Processing')}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showEdit && matterId && (
        <EditCaseModal
          matter={matter}
          lang={lang as 'zh' | 'en'}
          t={t}
          matterId={matterId}
          onClose={() => setShowEdit(false)}
          onSaved={(m) => {
            onMatterUpdated(m)
            setShowEdit(false)
          }}
        />
      )}
    </div>
  )
}

function InfoField({ label, value, bold, mono }: { label: string; value: string; bold?: boolean; mono?: boolean }) {
  return (
    <div>
      <div className="text-[11px] text-brand-400 mb-0.5">{label}</div>
      <div className={`text-xs text-brand-700 ${bold ? 'font-semibold' : ''} ${mono ? 'font-mono' : ''}`}>
        {value}
      </div>
    </div>
  )
}

// ─── 编辑案件信息弹窗 ──────────────────────────────────────────────────────────

const _KNOWN_PA = new Set(CASE_TYPES.map((c) => c.value))
const _KNOWN_CL = new Set(COURT_LOCATIONS.map((c) => c.value))
const _CUSTOM_PA_KEY = 'lawyerapp_custom_practice_areas'
const _CUSTOM_CL_KEY = 'lawyerapp_custom_court_locations'

function _getCustomHistory(storageKey: string): string[] {
  try { return JSON.parse(localStorage.getItem(storageKey) ?? '[]') as string[] }
  catch { return [] }
}

function _saveCustomHistory(storageKey: string, value: string): void {
  const v = value.trim()
  if (!v) return
  try {
    const prev = _getCustomHistory(storageKey)
    localStorage.setItem(storageKey, JSON.stringify([v, ...prev.filter((x) => x !== v)].slice(0, 5)))
  } catch { /* ignore */ }
}

function EditCaseModal({
  matter,
  lang,
  t,
  matterId,
  onClose,
  onSaved,
}: {
  matter: Matter
  lang: 'zh' | 'en'
  t: (typeof LABELS)['zh']
  matterId: string
  onClose: () => void
  onSaved: (m: Matter) => void
}) {
  const initPA = matter.practice_area ?? ''
  const initCL = matter.court_location ?? ''

  const [title, setTitle] = useState(matter.title)
  const [caseNumber, setCaseNumber] = useState(matter.case_number ?? '')
  const [practiceArea, setPracticeArea] = useState(_KNOWN_PA.has(initPA) ? initPA : (initPA ? 'other' : ''))
  const [customPracticeArea, setCustomPracticeArea] = useState(_KNOWN_PA.has(initPA) ? '' : initPA)
  const [court, setCourt] = useState(matter.court ?? '')
  const [courtLocation, setCourtLocation] = useState(_KNOWN_CL.has(initCL) ? initCL : (initCL ? 'other' : ''))
  const [customCourtLocation, setCustomCourtLocation] = useState(_KNOWN_CL.has(initCL) ? '' : initCL)
  const [jurisdiction, setJurisdiction] = useState(matter.jurisdiction ?? '')
  const [clientName, setClientName] = useState(matter.client_name ?? '')
  const [opposingParty, setOpposingParty] = useState(matter.opposing_party ?? '')
  const [summary, setSummary] = useState(matter.summary ?? '')
  const [saving, setSaving] = useState(false)

  const [recentPA] = useState(() => _getCustomHistory(_CUSTOM_PA_KEY))
  const [recentCL] = useState(() => _getCustomHistory(_CUSTOM_CL_KEY))

  const handleSave = () => {
    const finalPracticeArea = practiceArea === 'other' ? customPracticeArea.trim() : practiceArea
    const finalCourtLocation = courtLocation === 'other' ? customCourtLocation.trim() : courtLocation
    if (practiceArea === 'other' && customPracticeArea.trim()) _saveCustomHistory(_CUSTOM_PA_KEY, customPracticeArea)
    if (courtLocation === 'other' && customCourtLocation.trim()) _saveCustomHistory(_CUSTOM_CL_KEY, customCourtLocation)
    setSaving(true)
    fetch(`/matters/${matterId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        title: title.trim(),
        case_number: caseNumber.trim() || null,
        practice_area: finalPracticeArea || null,
        court: court.trim() || null,
        court_location: finalCourtLocation || null,
        jurisdiction: jurisdiction.trim() || null,
        client_name: clientName.trim() || null,
        opposing_party: opposingParty.trim() || null,
        summary: summary.trim() || null,
      }),
    })
      .then((r) => r.json())
      .then((updated: Matter) => onSaved(updated))
      .catch(() => setSaving(false))
  }

  const inputCls = 'w-full px-3 py-2 rounded-lg border border-brand-200 text-sm focus:outline-none focus:border-blue-400'
  const labelCls = 'text-xs font-medium text-brand-600 mb-1 block'

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto mx-4">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-brand-800">{t.editCase}</h3>
            <button type="button" onClick={onClose} className="p-1 rounded-lg text-brand-400 hover:bg-brand-100">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label className={labelCls}>{t.caseName}</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} />
            </div>

            <div>
              <label className={labelCls}>{t.caseNumber}</label>
              <input value={caseNumber} onChange={(e) => setCaseNumber(e.target.value)} className={`${inputCls} font-mono`} />
            </div>

            <div>
              <label className={labelCls}>{t.caseType}</label>
              <select
                value={practiceArea}
                onChange={(e) => setPracticeArea(e.target.value)}
                className={`${inputCls} bg-white`}
              >
                <option value="">{lang === 'zh' ? '选择类型' : 'Select type'}</option>
                {CASE_TYPES.map((ct) => (
                  <option key={ct.value} value={ct.value}>{lang === 'zh' ? ct.zh : ct.en}</option>
                ))}
              </select>
            </div>

            {practiceArea === 'other' && (
              <div>
                <label className={labelCls}>{t.customType}</label>
                <input
                  value={customPracticeArea}
                  onChange={(e) => setCustomPracticeArea(e.target.value)}
                  placeholder={t.customTypePlaceholder}
                  className={inputCls}
                  autoFocus
                />
                {recentPA.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px] text-brand-400">{t.recentUsed}</span>
                    {recentPA.map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setCustomPracticeArea(v)}
                        className="text-[11px] px-2 py-0.5 rounded-full bg-brand-100 text-brand-600 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div>
              <label className={labelCls}>{t.courtName}</label>
              <input value={court} onChange={(e) => setCourt(e.target.value)} className={inputCls} />
            </div>

            <div>
              <label className={labelCls}>{t.courtLocation}</label>
              <select
                value={courtLocation}
                onChange={(e) => setCourtLocation(e.target.value)}
                className={`${inputCls} bg-white`}
              >
                <option value="">{lang === 'zh' ? '选择法院所在地' : 'Select court location'}</option>
                {COURT_LOCATIONS.map((loc) => (
                  <option key={loc.value} value={loc.value}>{lang === 'zh' ? loc.zh : loc.en}</option>
                ))}
              </select>
            </div>

            {courtLocation === 'other' && (
              <div>
                <label className={labelCls}>{t.customLocation}</label>
                <input
                  value={customCourtLocation}
                  onChange={(e) => setCustomCourtLocation(e.target.value)}
                  placeholder={t.customLocationPlaceholder}
                  className={inputCls}
                  autoFocus
                />
                {recentCL.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    <span className="text-[11px] text-brand-400">{t.recentUsed}</span>
                    {recentCL.map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setCustomCourtLocation(v)}
                        className="text-[11px] px-2 py-0.5 rounded-full bg-brand-100 text-brand-600 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div>
              <label className={labelCls}>{t.jurisdiction}</label>
              <input value={jurisdiction} onChange={(e) => setJurisdiction(e.target.value)} className={inputCls} />
            </div>

            <div>
              <label className={labelCls}>{t.clientName}</label>
              <input value={clientName} onChange={(e) => setClientName(e.target.value)} className={inputCls} />
            </div>

            <div>
              <label className={labelCls}>{t.opposingParty}</label>
              <input value={opposingParty} onChange={(e) => setOpposingParty(e.target.value)} className={inputCls} />
            </div>

            <div>
              <label className={labelCls}>{t.caseSummary}</label>
              <textarea
                value={summary}
                onChange={(e) => setSummary(e.target.value)}
                rows={3}
                className={`${inputCls} resize-none`}
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm text-brand-600 hover:bg-brand-100 transition-colors"
            >
              {t.cancel}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || !title.trim()}
              className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {saving ? (lang === 'zh' ? '保存中...' : 'Saving...') : t.save}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Export selection helpers ────────────────────────────────────────────────

interface QAPair {
  pairIndex: number
  userTurn: ChatTurn
  assistantTurn?: ChatTurn
}

function groupTurnsIntoPairs(turns: ChatTurn[]): QAPair[] {
  const pairs: QAPair[] = []
  let i = 0
  while (i < turns.length) {
    if (turns[i].role === 'user') {
      const userTurn = turns[i]
      const assistantTurn = turns[i + 1]?.role === 'assistant' ? turns[i + 1] : undefined
      pairs.push({ pairIndex: pairs.length, userTurn, assistantTurn })
      i += assistantTurn ? 2 : 1
    } else {
      i++
    }
  }
  return pairs
}

function ExportSelectModal({
  turns,
  lang,
  onConfirm,
  onCancel,
}: {
  turns: ChatTurn[]
  lang: 'zh' | 'en'
  onConfirm: (selectedTurns: ChatTurn[]) => void
  onCancel: () => void
}) {
  const pairs = useMemo(() => groupTurnsIntoPairs(turns), [turns])
  const [selected, setSelected] = useState<Set<number>>(
    () => new Set(pairs.map((p) => p.pairIndex)),
  )

  const allSel = selected.size === pairs.length
  const noneSel = selected.size === 0

  const toggle = (idx: number) =>
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(idx) ? next.delete(idx) : next.add(idx)
      return next
    })

  const toggleAll = () =>
    setSelected(allSel ? new Set() : new Set(pairs.map((p) => p.pairIndex)))

  const handleConfirm = () => {
    const out: ChatTurn[] = []
    for (const pair of pairs) {
      if (selected.has(pair.pairIndex)) {
        out.push(pair.userTurn)
        if (pair.assistantTurn) out.push(pair.assistantTurn)
      }
    }
    onConfirm(out)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col mx-4">
        {/* Header */}
        <div className="p-5 border-b border-brand-100">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-brand-800">
              {lang === 'zh' ? '选择导出内容' : 'Select Content to Export'}
            </h3>
            <button type="button" onClick={onCancel} className="p-1 rounded text-brand-400 hover:bg-brand-100">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="text-xs text-brand-400">
              {lang === 'zh'
                ? `共 ${pairs.length} 轮对话，已选 ${selected.size} 轮`
                : `${pairs.length} exchanges · ${selected.size} selected`}
            </span>
            <button type="button" onClick={toggleAll} className="text-xs text-blue-600 hover:underline">
              {allSel
                ? (lang === 'zh' ? '取消全选' : 'Deselect All')
                : (lang === 'zh' ? '全选' : 'Select All')}
            </button>
          </div>
        </div>

        {/* Turn list */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {pairs.map((pair) => {
            const sel = selected.has(pair.pairIndex)
            const q = pair.userTurn.content
            const preview = q.length > 90 ? q.slice(0, 90) + '…' : q
            return (
              <label
                key={pair.pairIndex}
                className={`flex gap-3 p-3 rounded-xl border cursor-pointer transition-colors ${
                  sel
                    ? 'bg-blue-50 border-blue-200'
                    : 'bg-brand-50 border-brand-200 hover:border-brand-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={sel}
                  onChange={() => toggle(pair.pairIndex)}
                  className="mt-0.5 w-4 h-4 rounded border-brand-300 text-blue-600 focus:ring-blue-400 flex-shrink-0"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-[10px] font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded-full">
                      Q{pair.pairIndex + 1}
                    </span>
                    {pair.assistantTurn ? (
                      <span className="text-[10px] text-brand-500 bg-brand-100 px-1.5 py-0.5 rounded-full">
                        {lang === 'zh' ? '含AI回复' : '+ AI Reply'}
                      </span>
                    ) : (
                      <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">
                        {lang === 'zh' ? '仅提问' : 'Q only'}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-brand-600 leading-snug line-clamp-2">{preview}</p>
                </div>
              </label>
            )
          })}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-brand-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm text-brand-600 hover:bg-brand-100 transition-colors"
          >
            {lang === 'zh' ? '取消' : 'Cancel'}
          </button>
          <button
            type="button"
            disabled={noneSel}
            onClick={handleConfirm}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            {lang === 'zh'
              ? `导出 ${selected.size} 轮对话`
              : `Export ${selected.size} exchange(s)`}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── AI 对话 Tab ─────────────────────────────────────────────────────────────
function ChatTab({
  chatHook,
  chatSettings,
  conversations,
  activeConvId,
  loadedTurns,
  localConvId,
  localBaseTurns,
  setActiveConvId,
  setLocalConvId,
  setLocalBaseTurns,
  setStreamConvId,
  generationConvIdRef,
  onSelectConv,
  onNewConv,
  onDeleteConv,
  matterId,
  setLoadedTurns,
  loadConversations,
  updateConversationSummary,
  t,
}: {
  chatHook: ReturnType<typeof useChat>
  chatSettings: ChatSettings
  conversations: ConversationSummary[]
  activeConvId: string | null
  loadedTurns: ChatTurn[]
  localConvId: string | null
  localBaseTurns: ChatTurn[]
  setActiveConvId: Dispatch<SetStateAction<string | null>>
  setLocalConvId: Dispatch<SetStateAction<string | null>>
  setLocalBaseTurns: Dispatch<SetStateAction<ChatTurn[]>>
  setStreamConvId: Dispatch<SetStateAction<string | null>>
  generationConvIdRef: { current: string | null }
  onSelectConv: (convId: string) => void
  onNewConv: () => void
  onDeleteConv: (convId: string) => void
  matterId?: string
  setLoadedTurns: Dispatch<SetStateAction<ChatTurn[]>>
  loadConversations: () => void
  updateConversationSummary: (detail: ConversationDetail) => void
  t: (typeof LABELS)['zh']
}) {
  const {
    history,
    pending,
    error,
    isStreaming,
    send,
    stop,
    attachedDocs,
    setAttachedDocs,
    setHistory,
  } = chatHook
  const { s, lang } = useI18n()
  const bottomRef = useRef<HTMLDivElement>(null)
  const [deletingTurn, setDeletingTurn] = useState<number | null>(null)
  const [isPreparingSend, setIsPreparingSend] = useState(false)
  const [showExportConfirm, setShowExportConfirm] = useState(false)
  const [showExportSelect, setShowExportSelect] = useState(false)
  const [exportTurns, setExportTurns] = useState<ChatTurn[]>([])
  const composerRef = useRef<ComposerHandle>(null)

  // ─── 对话侧栏宽度拖拽 ─────────────────────────────────────────────────
  const [convSidebarWidth, setConvSidebarWidth] = useState(208)
  const convDragRef = useRef<{ startX: number; startW: number } | null>(null)

  const handleConvDragStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    convDragRef.current = { startX: e.clientX, startW: convSidebarWidth }
    const onMove = (ev: MouseEvent) => {
      if (!convDragRef.current) return
      setConvSidebarWidth(Math.max(140, convDragRef.current.startW + ev.clientX - convDragRef.current.startX))
    }
    const onUp = () => {
      convDragRef.current = null
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }, [convSidebarWidth])

  // ─── 重命名 ───────────────────────────────────────────────────────────
  const [renamingConvId, setRenamingConvId] = useState<string | null>(null)
  const [renameTitle, setRenameTitle] = useState('')
  const renameInputRef = useRef<HTMLInputElement>(null)

  const handleStartRename = useCallback((convId: string, currentTitle: string) => {
    setRenamingConvId(convId)
    setRenameTitle(currentTitle)
    setTimeout(() => renameInputRef.current?.select(), 0)
  }, [])

  const handleSubmitRename = useCallback((convId: string) => {
    const title = renameTitle.trim()
    if (!title || !matterId) { setRenamingConvId(null); return }
    fetch(`/matters/${matterId}/conversations/${convId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    })
      .then((r) => { if (r.ok) loadConversations() })
      .catch(() => {})
      .finally(() => setRenamingConvId(null))
  }, [renameTitle, matterId, loadConversations])

  const handleDeleteTurn = useCallback(
    async (turnIndex: number) => {
      if (deletingTurn !== null) return
      if (!window.confirm(s.deleteTurnConfirm)) return
      setDeletingTurn(turnIndex)
      const shouldPersistDeletion =
        Boolean(matterId && activeConvId && turnIndex < loadedTurns.length)
      if (shouldPersistDeletion) {
        try {
          const res = await fetch(
            `/matters/${matterId}/conversations/${activeConvId}/turns/${turnIndex}`,
            { method: 'DELETE' },
          )
          if (!res.ok) {
            const text = await res.text().catch(() => '')
            throw new Error(text || res.statusText)
          }
          const conv: ConversationDetail = await res.json()
          const turns = conv.turns.map((turn) => ({
            role: turn.role as 'user' | 'assistant',
            content: turn.content,
            reasoning: turn.reasoning ?? undefined,
            steps: turn.steps ?? undefined,
            sources: turn.sources ?? undefined,
            stats: turn.stats ?? undefined,
            verification: turn.verification ?? undefined,
          }))
          setLoadedTurns(turns)
          loadConversations()
        } catch (err) {
          console.error('Failed to delete turn', err)
          window.alert(s.deleteTurnFailed)
        } finally {
          setDeletingTurn(null)
        }
        return
      }

      const removedFromLoadedRef = { value: false }
      setLoadedTurns((prev) => {
        if (turnIndex >= prev.length) return prev
        removedFromLoadedRef.value = true
        return prev.filter((_, idx) => idx !== turnIndex)
      })
      if (!removedFromLoadedRef.value) {
        const historyIndex = turnIndex - loadedTurns.length
        if (historyIndex >= 0) {
          setHistory((prev) => prev.filter((_, idx) => idx !== historyIndex))
        }
      }
      setDeletingTurn(null)
    },
    [
      activeConvId,
      deletingTurn,
      loadedTurns.length,
      loadConversations,
      matterId,
      s,
      setHistory,
      setLoadedTurns,
    ],
  )

  // 当前正在生成的一轮属于 localConvId；切换到其他历史时只展示对应历史，不展示/清空后台流。
  const showingLiveConversation = activeConvId === localConvId
  const visibleLoadedTurns = showingLiveConversation ? localBaseTurns : loadedTurns
  const visibleHistory = showingLiveConversation
    ? history.filter((turn) => turn.role === 'assistant')
    : []
  const visiblePending = showingLiveConversation ? pending : null
  const visibleError = showingLiveConversation ? error : null
  const allTurns = [...visibleLoadedTurns, ...visibleHistory]

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [allTurns.length, visiblePending?.partial, visiblePending?.sources.length])

  const isEmpty = allTurns.length === 0 && !visiblePending && !visibleError

  const handleSend = useCallback(async (text: string) => {
    const question = text.trim()
    if (!question || isStreaming || isPreparingSend || !matterId) return

    setIsPreparingSend(true)
    try {
      let targetConvId = activeConvId
      let baseTurns = loadedTurns

      if (!targetConvId) {
        const createRes = await fetch(`/matters/${matterId}/conversations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: question.slice(0, 50) }),
        })
        if (!createRes.ok) throw new Error(await createRes.text().catch(() => createRes.statusText))
        const conv: ConversationDetail = await createRes.json()
        targetConvId = conv.conv_id
        setActiveConvId(conv.conv_id)
        baseTurns = []
      }

      const userTurn: ChatTurn = { role: 'user', content: question }
      const appendRes = await fetch(`/matters/${matterId}/conversations/${targetConvId}/turns`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          turns: [{ role: 'user', content: question, reasoning: null }],
        }),
      })
      if (!appendRes.ok) throw new Error(await appendRes.text().catch(() => appendRes.statusText))
      const updatedConv: ConversationDetail = await appendRes.json()

      const liveBaseTurns = [...baseTurns, userTurn]
      setLoadedTurns(liveBaseTurns)
      setLocalConvId(targetConvId)
      setLocalBaseTurns(liveBaseTurns)
      setStreamConvId(targetConvId)
      generationConvIdRef.current = targetConvId
      updateConversationSummary(updatedConv)

      await send(question, chatSettings, baseTurns, matterId)
    } catch (err) {
      console.error('Failed to start persisted chat turn', err)
    } finally {
      setIsPreparingSend(false)
    }
  }, [
    activeConvId,
    chatSettings,
    generationConvIdRef,
    isPreparingSend,
    isStreaming,
    loadedTurns,
    matterId,
    send,
    setActiveConvId,
    setLocalBaseTurns,
    setLocalConvId,
    setStreamConvId,
    updateConversationSummary,
  ])

  return (
    <div className="flex-1 flex min-h-0">
      {/* 左侧对话历史列表 — 宽度可拖拽 */}
      <div style={{ width: convSidebarWidth }} className="border-r border-brand-200 bg-white flex flex-col flex-shrink-0">
        <div className="p-3">
          <button
            type="button"
            onClick={onNewConv}
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            {t.newConversation}
          </button>
        </div>
        <div className="px-3 pb-2">
          <h5 className="text-[11px] font-medium text-brand-400 uppercase tracking-wider">
            {t.historyConversations}
          </h5>
        </div>
        <div className="flex-1 overflow-y-auto px-2 pb-2">
          {conversations.length === 0 ? (
            <div className="text-center text-xs text-brand-400 py-6">
              {t.noConversations}
            </div>
          ) : (
            conversations.map((conv) => {
              const isActive = conv.conv_id === activeConvId
              const dateStr = conv.updated_at?.split('T')[0] ?? ''
              return (
                <div
                  key={conv.conv_id}
                  className={`group/conv relative w-full text-left px-2.5 py-2 rounded-lg mb-1 transition-colors cursor-pointer ${
                    isActive
                      ? 'bg-blue-50 border border-blue-200'
                      : 'hover:bg-brand-50 border border-transparent'
                  }`}
                  onClick={() => onSelectConv(conv.conv_id)}
                >
                  <div className="flex items-start gap-2">
                    <MessageSquare className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${isActive ? 'text-blue-600' : 'text-brand-400'}`} />
                    <div className="flex-1 min-w-0">
                      <div className={`text-xs font-medium truncate ${isActive ? 'text-blue-800' : 'text-brand-700'}`}>
                        {renamingConvId === conv.conv_id ? (
                          <input
                            ref={renameInputRef}
                            value={renameTitle}
                            onChange={(e) => setRenameTitle(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSubmitRename(conv.conv_id)
                              if (e.key === 'Escape') setRenamingConvId(null)
                            }}
                            onBlur={() => handleSubmitRename(conv.conv_id)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full text-xs px-1 py-0.5 border border-blue-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                            autoFocus
                          />
                        ) : (
                          conv.title || '(untitled)'
                        )}
                      </div>
                      <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-brand-400">
                        <Clock className="w-3 h-3" />
                        <span>{dateStr}</span>
                        <span>·</span>
                        <span>{Math.ceil(conv.turn_count / 2)} {t.turnsCount}</span>
                      </div>
                    </div>
                    <div className="absolute top-1.5 right-1.5 flex items-center gap-0.5 opacity-0 group-hover/conv:opacity-100 transition-all">
                      <button
                        type="button"
                        title={lang === 'zh' ? '重命名' : 'Rename'}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleStartRename(conv.conv_id, conv.title)
                        }}
                        className="p-1 rounded text-brand-300 hover:text-blue-500 hover:bg-blue-50"
                      >
                        <Pencil className="w-3 h-3" />
                      </button>
                      <button
                        type="button"
                        title={t.deleteConv}
                        onClick={(e) => {
                          e.stopPropagation()
                          if (window.confirm(t.deleteConvConfirm)) onDeleteConv(conv.conv_id)
                        }}
                        className="p-1 rounded text-brand-300 hover:text-red-500 hover:bg-red-50"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </div>

      {/* 对话侧栏拖拽分隔条 */}
      <div
        onMouseDown={handleConvDragStart}
        className="w-1 flex-shrink-0 cursor-col-resize bg-brand-200 hover:bg-blue-400 transition-colors select-none"
      />

      {/* 右侧聊天区 */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* 导出按钮栏 */}
        {allTurns.length > 0 && (
          <div className="flex items-center justify-end px-4 py-2 border-b border-brand-100 bg-white">
            <button
              type="button"
              onClick={() => setShowExportSelect(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-brand-600 border border-brand-200 hover:bg-brand-50 hover:border-brand-300 transition-colors"
            >
              <Download className="w-3.5 h-3.5" />
              {lang === 'zh' ? '导出为Word' : 'Export to Word'}
            </button>
          </div>
        )}
        {showExportSelect && (
          <ExportSelectModal
            turns={allTurns}
            lang={lang as 'zh' | 'en'}
            onConfirm={(selected) => {
              setExportTurns(selected)
              setShowExportSelect(false)
              setShowExportConfirm(true)
            }}
            onCancel={() => setShowExportSelect(false)}
          />
        )}
        <AIConfirmModal
          open={showExportConfirm}
          actionType="export_word"
          onConfirm={() => {
            setShowExportConfirm(false)
            logAIContentConfirmation({
              action_type: 'export_word',
              matter_id: matterId,
              conv_id: activeConvId,
            })
            exportChatToDocx({
              title: conversations.find((c) => c.conv_id === activeConvId)?.title || (lang === 'zh' ? 'AI对话记录' : 'AI Chat Export'),
              caseNumber: matterId,
              turns: exportTurns,
              lang: lang as 'zh' | 'en',
            })
          }}
          onCancel={() => setShowExportConfirm(false)}
        />
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-3xl mx-auto px-6 py-6">
            {isEmpty ? (
              <div className="text-center py-16 text-brand-400 text-sm">
                <MessageSquare className="w-10 h-10 mx-auto mb-3 text-brand-300" />
                <p>{s.emptyDescBefore} AI {s.emptyDescAfter}</p>
              </div>
            ) : (
              <>
                {/* 已加载的历史 turns（只读展示） */}
                {visibleLoadedTurns.map((turn, i) => (
                  <MessageBubble
                    key={`loaded-${i}`}
                    role={turn.role}
                    content={turn.content}
                    reasoning={turn.reasoning}
                    steps={turn.steps}
                    sources={turn.sources}
                    stats={turn.stats}
                    verification={turn.verification}
                    onDelete={() => handleDeleteTurn(i)}
                    onFillInput={(q) => composerRef.current?.fill(q)}
                  />
                ))}
                {/* 当前会话的新 turns */}
                {visibleHistory.map((turn, i) => (
                  <MessageBubble
                    key={`new-${i}`}
                    role={turn.role}
                    content={turn.content}
                    reasoning={turn.reasoning}
                    steps={turn.steps}
                    sources={turn.sources}
                    stats={turn.stats}
                    verification={turn.verification}
                    onDelete={() => handleDeleteTurn(visibleLoadedTurns.length + i)}
                    onFillInput={(q) => composerRef.current?.fill(q)}
                  />
                ))}
                {visiblePending && (
                  <MessageBubble
                    role="assistant"
                    content={visiblePending.partial}
                    reasoning={visiblePending.reasoning}
                    steps={visiblePending.steps}
                    sources={visiblePending.sources}
                    stats={visiblePending.stats}
                    verification={visiblePending.verification}
                    isStreaming
                  />
                )}
                {visibleError && (
                  <div className="my-3 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                    <span className="font-semibold">{s.errorLabel}:</span> {visibleError}
                  </div>
                )}
                <div ref={bottomRef} />
              </>
            )}
          </div>
        </div>
        <Composer
          ref={composerRef}
          onSend={handleSend}
          onStop={stop}
          isStreaming={isStreaming}
          attachedDocs={attachedDocs}
          onAttachmentsChange={setAttachedDocs}
        />
      </div>
    </div>
  )
}

// ─── 文书生成 Tab ────────────────────────────────────────────────────────────

function DocTypeSelectModal({
  docTypes,
  matterFiles,
  lang,
  t,
  onConfirm,
  onCancel,
}: {
  docTypes: DocTypeItem[]
  matterFiles: MatterFileItem[]
  lang: 'zh' | 'en'
  t: (typeof LABELS)['zh']
  onConfirm: (docType: string, instructions: string, fileIds: string[]) => void
  onCancel: () => void
}) {
  const [selected, setSelected] = useState<string | null>(null)
  const [instructions, setInstructions] = useState('')
  const [selectedFileIds, setSelectedFileIds] = useState<Set<string>>(new Set())

  const availableFiles = matterFiles.filter(
    (f) => f.processing_status === 'done' && f.text_chars > 0,
  )

  const toggleFile = (fileId: string) => {
    setSelectedFileIds((prev) => {
      const next = new Set(prev)
      if (next.has(fileId)) next.delete(fileId)
      else next.add(fileId)
      return next
    })
  }

  const toggleAll = () => {
    if (selectedFileIds.size === availableFiles.length) {
      setSelectedFileIds(new Set())
    } else {
      setSelectedFileIds(new Set(availableFiles.map((f) => f.file_id)))
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onCancel} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col mx-4">
        <div className="p-5 border-b border-brand-100">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-brand-800">{t.docTypeTitle}</h3>
            <button type="button" onClick={onCancel} className="p-1 rounded text-brand-400 hover:bg-brand-100">
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-brand-400 mt-1">{t.docTypeDesc}</p>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* 文书类型选择 */}
          <div className="space-y-2">
            {docTypes.map((dt) => {
              const sel = selected === dt.key
              const name = lang === 'zh' ? dt.name_zh : dt.name_en
              const desc = lang === 'zh' ? dt.desc_zh : dt.desc_en
              return (
                <button
                  key={dt.key}
                  type="button"
                  onClick={() => setSelected(dt.key)}
                  className={`w-full text-left p-3 rounded-xl border transition-colors ${
                    sel
                      ? 'bg-blue-50 border-blue-300 ring-1 ring-blue-200'
                      : 'bg-brand-50 border-brand-200 hover:border-brand-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <FileText className={`w-4 h-4 ${sel ? 'text-blue-600' : 'text-brand-400'}`} />
                    <span className={`text-sm font-semibold ${sel ? 'text-blue-700' : 'text-brand-700'}`}>{name}</span>
                  </div>
                  <p className="text-xs text-brand-400 leading-snug">{desc}</p>
                </button>
              )
            })}
          </div>

          {/* 案件材料选择 */}
          {selected && (
            <div className="border-t border-brand-100 pt-3">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <label className="text-xs font-semibold text-brand-700">{t.docSelectFiles}</label>
                  <p className="text-[11px] text-brand-400">{t.docSelectFilesDesc}</p>
                </div>
                {availableFiles.length > 0 && (
                  <button
                    type="button"
                    onClick={toggleAll}
                    className="text-xs text-blue-600 hover:underline"
                  >
                    {t.docSelectAll} ({selectedFileIds.size}/{availableFiles.length})
                  </button>
                )}
              </div>
              {availableFiles.length === 0 ? (
                <div className="text-xs text-brand-400 bg-brand-50 rounded-lg p-3 text-center">
                  {t.docNoFiles}
                </div>
              ) : (
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {availableFiles.map((file) => {
                    const checked = selectedFileIds.has(file.file_id)
                    return (
                      <label
                        key={file.file_id}
                        className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                          checked ? 'bg-blue-50' : 'hover:bg-brand-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleFile(file.file_id)}
                          className="w-3.5 h-3.5 rounded border-brand-300 text-blue-600 focus:ring-blue-400"
                        />
                        <FileText className="w-3.5 h-3.5 text-brand-400 flex-shrink-0" />
                        <span className="text-xs text-brand-700 truncate flex-1">{file.filename}</span>
                        <span className="text-[10px] text-brand-400 flex-shrink-0">
                          {(file.size_bytes / 1024).toFixed(0)}KB
                        </span>
                      </label>
                    )
                  })}
                </div>
              )}
              {selectedFileIds.size > 0 && (
                <p className="text-[11px] text-blue-600 mt-1">
                  {selectedFileIds.size} {t.docFilesSelected}
                </p>
              )}
            </div>
          )}

          {/* 额外要求 */}
          {selected && (
            <div className="border-t border-brand-100 pt-3">
              <label className="text-xs font-medium text-brand-600">{t.docInstructions}</label>
              <textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder={t.docInstructionsPlaceholder}
                rows={2}
                className="w-full mt-1 px-3 py-2 rounded-lg border border-brand-200 text-sm text-brand-700 placeholder:text-brand-400 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 resize-none"
              />
            </div>
          )}
        </div>

        <div className="p-4 border-t border-brand-100 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm text-brand-600 hover:bg-brand-100 transition-colors"
          >
            {t.docCancel}
          </button>
          <button
            type="button"
            disabled={!selected}
            onClick={() => selected && onConfirm(selected, instructions.trim(), Array.from(selectedFileIds))}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Sparkles className="w-3.5 h-3.5" />
            {t.generate}
          </button>
        </div>
      </div>
    </div>
  )
}

function DocsTab({
  matterId,
  lang,
  docLang,
  t,
}: {
  matterId: string
  lang: 'zh' | 'en'
  docLang: 'zh' | 'en'
  t: (typeof LABELS)['zh']
}) {
  const [docTypes, setDocTypes] = useState<DocTypeItem[]>([])
  const [documents, setDocuments] = useState<MatterDocument[]>([])
  const [matterFiles, setMatterFiles] = useState<MatterFileItem[]>([])
  const [showSelect, setShowSelect] = useState(false)
  const [generating, setGenerating] = useState(false)
  const [genError, setGenError] = useState<string | null>(null)
  const [viewDoc, setViewDoc] = useState<MatterDocument | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<MatterDocument | null>(null)
  const [showExportConfirm, setShowExportConfirm] = useState(false)
  const [exportTarget, setExportTarget] = useState<MatterDocument | null>(null)
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const loadDocs = useCallback(async () => {
    try {
      const docs = await fetchMatterDocuments(matterId)
      setDocuments(docs)
    } catch {
      /* best-effort */
    }
  }, [matterId])

  useEffect(() => {
    fetchDocumentTypes().then(setDocTypes).catch(() => {})
    loadDocs()
    fetch(`/matters/${matterId}/files`)
      .then((r) => r.json())
      .then((data: { items?: MatterFileItem[] }) => {
        if (data.items) setMatterFiles(data.items)
      })
      .catch(() => {})
  }, [loadDocs, matterId])

  const handleGenerate = async (docType: string, instructions: string, fileIds: string[]) => {
    setShowSelect(false)
    setGenerating(true)
    setGenError(null)
    try {
      await generateMatterDocument(
        matterId, docType, instructions || undefined, docLang,
        fileIds.length > 0 ? fileIds : undefined,
      )
      await loadDocs()
    } catch (err) {
      setGenError(err instanceof Error ? err.message : String(err))
    } finally {
      setGenerating(false)
    }
  }

  const handleCopy = async (doc: MatterDocument) => {
    if (!doc.content) return
    try {
      await navigator.clipboard.writeText(doc.content)
      setCopiedId(doc.doc_id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch {
      /* ignore */
    }
  }

  const handleExport = (doc: MatterDocument) => {
    setExportTarget(doc)
    setShowExportConfirm(true)
  }

  const handleConfirmExport = () => {
    setShowExportConfirm(false)
    if (!exportTarget || !exportTarget.content) return
    logAIContentConfirmation({
      action_type: 'export_word',
      matter_id: matterId,
    })
    exportDocToDocx({
      title: exportTarget.title,
      content: exportTarget.content,
      lang,
    })
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    try {
      await deleteMatterDocument(matterId, deleteTarget.doc_id)
      setDeleteTarget(null)
      await loadDocs()
    } catch {
      /* ignore */
    }
  }

  const docTypeName = (key: string) => {
    const dt = docTypes.find((d) => d.key === key)
    return dt ? (lang === 'zh' ? dt.name_zh : dt.name_en) : key
  }

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-3xl">
        {/* 生成区域 */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-5 text-white mb-6">
          <h3 className="text-base font-semibold mb-1">{t.docGenTitle}</h3>
          <p className="text-sm text-white/80 mb-4">{t.docGenDesc}</p>
          <button
            type="button"
            onClick={() => setShowSelect(true)}
            disabled={generating}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/20 hover:bg-white/30 text-sm font-medium transition-colors disabled:opacity-50"
          >
            <Sparkles className="w-4 h-4" />
            {t.selectDocType}
          </button>
        </div>

        {/* 生成中状态 */}
        {generating && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4 flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
            <span className="text-sm text-blue-700">{t.generating}</span>
          </div>
        )}

        {/* 生成失败 */}
        {genError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4">
            <div className="flex items-start gap-2">
              <span className="text-sm text-red-700 font-medium">{t.docGenerateFailed}</span>
            </div>
            <p className="text-xs text-red-600 mt-1">{genError}</p>
          </div>
        )}

        {/* 已生成文书列表 */}
        <h4 className="text-sm font-semibold text-brand-800 mb-3">{t.generatedDocs}</h4>
        {documents.length === 0 && !generating ? (
          <div className="text-center py-12 text-brand-400 text-sm">
            <FileText className="w-10 h-10 mx-auto mb-3 text-brand-300" />
            {t.docEmpty}
          </div>
        ) : (
          <div className="space-y-3">
            {documents.map((doc) => (
              <div key={doc.doc_id} className="bg-white rounded-xl border border-brand-200 p-5">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <h5 className="text-sm font-semibold text-brand-800">{doc.title}</h5>
                      <span className="text-[10px] font-medium text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full">
                        {docTypeName(doc.doc_type)}
                      </span>
                    </div>
                    <span className="text-xs text-brand-400">
                      {t.generatedAt} {new Date(doc.created_at).toLocaleString(lang === 'zh' ? 'zh-CN' : 'en-US')}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => handleCopy(doc)}
                      className="p-1.5 rounded text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors"
                      title={t.copySuccess}
                    >
                      {copiedId === doc.doc_id ? <ClipboardCopy className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                    <button
                      type="button"
                      onClick={() => handleExport(doc)}
                      className="p-1.5 rounded text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors"
                      title={t.exportWord}
                    >
                      <Download className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteTarget(doc)}
                      className="p-1.5 rounded text-brand-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                      title={t.deleteDoc}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                {doc.status === 'generating' ? (
                  <div className="flex items-center gap-2 text-xs text-blue-600 bg-blue-50 rounded-lg p-3">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    {t.docStatusGenerating}
                  </div>
                ) : doc.status === 'failed' ? (
                  <div className="text-xs text-red-600 bg-red-50 rounded-lg p-3">
                    {t.docStatusFailed}: {doc.error_msg || 'Unknown error'}
                  </div>
                ) : (
                  <>
                    <div className="text-xs text-brand-500 leading-relaxed bg-brand-50 rounded-lg p-3 font-mono whitespace-pre-line line-clamp-6">
                      {doc.content || ''}
                    </div>
                    <div className="flex items-center justify-end mt-2">
                      <button
                        type="button"
                        onClick={() => setViewDoc(doc)}
                        className="text-xs text-blue-600 hover:underline"
                      >
                        {t.viewFullDoc} →
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        {/* 文书类型选择弹窗 */}
        {showSelect && (
          <DocTypeSelectModal
            docTypes={docTypes}
            matterFiles={matterFiles}
            lang={lang}
            t={t}
            onConfirm={handleGenerate}
            onCancel={() => setShowSelect(false)}
          />
        )}

        {/* 全文查看弹窗 */}
        {viewDoc && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setViewDoc(null)} />
            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col mx-4">
              <div className="p-5 border-b border-brand-100 flex items-center justify-between">
                <div>
                  <h3 className="text-base font-bold text-brand-800">{viewDoc.title}</h3>
                  <span className="text-xs text-brand-400">
                    {docTypeName(viewDoc.doc_type)} · {t.generatedAt} {new Date(viewDoc.created_at).toLocaleString(lang === 'zh' ? 'zh-CN' : 'en-US')}
                  </span>
                </div>
                <button type="button" onClick={() => setViewDoc(null)} className="p-1 rounded text-brand-400 hover:bg-brand-100">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-5">
                <div className="prose prose-sm max-w-none text-brand-700 whitespace-pre-wrap leading-relaxed">
                  {viewDoc.content}
                </div>
              </div>
              <div className="p-4 border-t border-brand-100 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => handleCopy(viewDoc)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm text-brand-600 hover:bg-brand-100 transition-colors"
                >
                  <Copy className="w-3.5 h-3.5" />
                  {t.copySuccess}
                </button>
                <button
                  type="button"
                  onClick={() => { handleExport(viewDoc); setViewDoc(null) }}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  {t.exportWord}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 删除确认弹窗 */}
        {deleteTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={() => setDeleteTarget(null)} />
            <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 mx-4">
              <h3 className="text-base font-bold text-brand-800 mb-2">{t.deleteDoc}</h3>
              <p className="text-sm text-brand-500 mb-4">{t.deleteDocConfirm}</p>
              <p className="text-sm font-medium text-brand-700 bg-brand-50 rounded-lg p-2 mb-4">{deleteTarget.title}</p>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setDeleteTarget(null)}
                  className="px-4 py-2 rounded-lg text-sm text-brand-600 hover:bg-brand-100 transition-colors"
                >
                  {t.docCancel}
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-red-500 hover:bg-red-600 transition-colors"
                >
                  {t.deleteDoc}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 导出确认弹窗 */}
        <AIConfirmModal
          open={showExportConfirm}
          actionType="export_word"
          onConfirm={handleConfirmExport}
          onCancel={() => setShowExportConfirm(false)}
        />
      </div>
    </div>
  )
}

// ─── 材料管理 Tab ────────────────────────────────────────────────────────────
const CATEGORY_LABELS: Record<string, { zh: string; en: string }> = {
  certificate: { zh: '证件', en: 'Certificate' },
  evidence: { zh: '证据', en: 'Evidence' },
  statement: { zh: '陈述书', en: 'Statement' },
  contract: { zh: '合同', en: 'Contract' },
  property: { zh: '产权文件', en: 'Property Doc' },
  other: { zh: '其他', en: 'Other' },
}

// ─── 知识库挂载管理弹窗 ────────────────────────────────────────────────────────
function KbManageModal({
  lang,
  allCategories,
  linkedKeys,
  onSave,
  onClose,
}: {
  lang: string
  allCategories: KnowledgeCategory[]
  linkedKeys: string[]
  onSave: (keys: string[]) => void
  onClose: () => void
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set(linkedKeys))

  const toggle = (key: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 mx-4 max-h-[80vh] flex flex-col">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-lg text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
        <h3 className="text-lg font-semibold text-brand-800 mb-1">
          {lang === 'zh' ? '管理挂载知识库' : 'Manage Knowledge Bases'}
        </h3>
        <p className="text-xs text-brand-400 mb-4">
          {lang === 'zh' ? '勾选需要关联到本案件的知识库分类' : 'Select categories to link to this matter'}
        </p>
        <div className="flex-1 overflow-y-auto space-y-1.5 mb-4">
          {allCategories.map((cat) => {
            const isLinked = selected.has(cat.key)
            const name = lang === 'zh' ? cat.name_zh : cat.name_en
            const desc = lang === 'zh' ? cat.desc_zh : cat.desc_en
            return (
              <div
                key={cat.key}
                onClick={() => toggle(cat.key)}
                className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                  isLinked
                    ? 'border-blue-300 bg-blue-50/50'
                    : 'border-brand-200 hover:border-brand-300 hover:bg-brand-50'
                }`}
              >
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                  isLinked ? 'bg-blue-600 border-blue-600' : 'border-brand-300'
                }`}>
                  {isLinked && (
                    <svg className="w-3 h-3 text-white" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-brand-800">{name}</div>
                  <div className="text-[11px] text-brand-400 truncate">{desc}</div>
                </div>
                <span className="text-[11px] text-brand-400 flex-shrink-0">
                  {cat.file_count} {lang === 'zh' ? '文件' : 'files'}
                </span>
              </div>
            )
          })}
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-brand-200 text-sm text-brand-600 hover:bg-brand-50 transition-colors"
          >
            {lang === 'zh' ? '取消' : 'Cancel'}
          </button>
          <button
            type="button"
            onClick={() => onSave(Array.from(selected))}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            {lang === 'zh' ? '保存' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}

interface MatterFileItem {
  file_id: string
  matter_id: string
  filename: string
  mime_type: string
  size_bytes: number
  category: string
  text_chars: number
  notes: string | null
  processing_status: 'pending' | 'processing' | 'done' | 'failed'
  created_at: string
  updated_at: string
}

function MaterialsTab({
  matterId,
  lang,
  t,
  onFilesChange,
  onPreview,
  pendingUploads,
  uploading,
  onUploadFiles,
}: {
  matterId: string
  lang: string
  t: (typeof LABELS)['zh']
  onFilesChange?: () => void
  onPreview?: (file: MatterFileItem) => void
  pendingUploads: PendingMatterUpload[]
  uploading: boolean
  onUploadFiles: (files: FileList | null) => void
}) {
  const [files, setFiles] = useState<MatterFileItem[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const loadFiles = useCallback(() => {
    const query = selectedCategory !== 'all' ? `?category=${selectedCategory}` : ''
    fetch(`/matters/${matterId}/files${query}`)
      .then((r) => r.json())
      .then((data: { items?: MatterFileItem[] }) => {
        if (data.items) {
          setFiles(data.items)
        }
      })
      .catch(() => {})
  }, [matterId, selectedCategory])

  useEffect(() => {
    loadFiles()
    const interval = setInterval(() => {
      console.log('[poll-tab] refreshing materials tab', matterId)
      loadFiles()
    }, 2000)
    return () => clearInterval(interval)
  }, [loadFiles, matterId])

  const handleDelete = async (fileId: string, filename: string) => {
    if (!window.confirm(`确定要删除 "${filename}" 吗？`)) return
    await fetch(`/matters/${matterId}/files/${fileId}`, { method: 'DELETE' })
    loadFiles()
    onFilesChange?.()
  }

  const handleCategoryChange = async (fileId: string, newCat: string) => {
    await fetch(`/matters/${matterId}/files/${fileId}?category=${newCat}`, { method: 'PATCH' })
    loadFiles()
  }

  const [reprocessingIds, setReprocessingIds] = useState<Set<string>>(new Set())

  const handleReprocess = async (fileId: string) => {
    setReprocessingIds((prev) => new Set(prev).add(fileId))
    try {
      const res = await fetch(`/matters/${matterId}/files/${fileId}/reprocess`, { method: 'POST' })
      if (res.ok) {
        loadFiles()
        onFilesChange?.()
      }
    } catch { /* ignore */ }
    setReprocessingIds((prev) => {
      const next = new Set(prev)
      next.delete(fileId)
      return next
    })
  }

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-3xl">
        {/* 头部：标题 + 筛选 + 上传按钮 */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-brand-800">{t.materialsTitle}</h3>
          <div className="flex items-center gap-2">
            {/* 分类筛选 */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-1.5 rounded-lg border border-brand-200 text-xs text-brand-600 focus:outline-none focus:border-blue-400"
            >
              <option value="all">{lang === 'zh' ? '全部分类' : 'All categories'}</option>
              {Object.entries(CATEGORY_LABELS).map(([key, labels]) => (
                <option key={key} value={key}>{lang === 'zh' ? labels.zh : labels.en}</option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <Plus className="w-4 h-4" />
              {uploading ? (lang === 'zh' ? '上传中...' : 'Uploading...') : t.uploadMaterial}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => onUploadFiles(e.target.files)}
            />
          </div>
        </div>

        {/* 拖拽上传区域 */}
        <div
          onDragOver={(e) => { e.preventDefault(); e.stopPropagation() }}
          onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onUploadFiles(e.dataTransfer.files) }}
          className="border-2 border-dashed border-brand-200 rounded-xl p-6 flex flex-col items-center justify-center mb-4 hover:border-blue-300 hover:bg-blue-50/30 transition-colors cursor-pointer"
          onClick={() => fileInputRef.current?.click()}
        >
          <Upload className="w-8 h-8 text-brand-300 mb-2" />
          <p className="text-sm text-brand-500">{lang === 'zh' ? '点击或拖拽文件到此处上传' : 'Click or drag files here'}</p>
          <p className="text-xs text-brand-400 mt-1">{lang === 'zh' ? '支持 PDF、Word、图片、Excel 等格式' : 'PDF, Word, images, Excel supported'}</p>
        </div>

        {pendingUploads.length > 0 && (
          <div className="space-y-2 mb-4">
            {pendingUploads.map((pending) => (
              <div
                key={pending.id}
                className="bg-white rounded-xl border border-dashed border-brand-200 p-4 flex items-center gap-3"
              >
                <Loader2 className="w-4 h-4 text-brand-500 animate-spin" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-brand-800 truncate">{pending.filename}</div>
                  <div className="text-[10px] text-brand-500 flex items-center gap-1">
                    <span>
                      {pending.status === 'uploading'
                        ? t.materialsPendingUploading
                        : pending.status === 'processing'
                        ? t.materialsPendingProcessing
                        : t.materialsUploadFailed}
                    </span>
                    <span>·</span>
                    <span>{formatSize(pending.size_bytes)}</span>
                  </div>
                  {pending.status === 'error' && pending.error && (
                    <div className="text-[10px] text-red-600 truncate">{pending.error}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 文件列表 */}
        {files.length === 0 ? (
          <div className="text-center text-brand-400 text-sm py-8">
            {lang === 'zh' ? '暂无案件材料' : 'No materials yet'}
          </div>
        ) : (
          <div className="space-y-2">
            {files.map((file) => {
              const ext = file.filename.split('.').pop()?.toUpperCase() ?? ''
              const iconColor =
                ext === 'PDF' ? 'text-red-400'
                : ['DOCX', 'DOC'].includes(ext) ? 'text-blue-400'
                : ['JPG', 'JPEG', 'PNG', 'GIF', 'WEBP'].includes(ext) ? 'text-green-400'
                : ['XLSX', 'XLS', 'CSV'].includes(ext) ? 'text-emerald-400'
                : 'text-brand-400'

              return (
                <div
                  key={file.file_id}
                  className="bg-white rounded-xl border border-brand-200 p-4 hover:border-brand-300 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <FileText className={`w-5 h-5 flex-shrink-0 ${iconColor}`} />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-brand-800 truncate">{file.filename}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        {/* 可切换分类标签 */}
                        <select
                          value={file.category}
                          onChange={(e) => handleCategoryChange(file.file_id, e.target.value)}
                          className="px-1.5 py-0.5 rounded border border-brand-200 text-[10px] text-brand-600 bg-brand-50 focus:outline-none"
                        >
                          {Object.entries(CATEGORY_LABELS).map(([key, labels]) => (
                            <option key={key} value={key}>{lang === 'zh' ? labels.zh : labels.en}</option>
                          ))}
                        </select>
                        <span className="text-xs text-brand-400">{ext} · {formatSize(file.size_bytes)}</span>
                        {file.processing_status === 'done' ? (
                          <span className="text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded">
                            {lang === 'zh' ? '已解析' : 'Parsed'}
                          </span>
                        ) : file.processing_status === 'failed' ? (
                          <span className="text-[10px] text-red-600 bg-red-50 px-1.5 py-0.5 rounded">
                            {t.materialsOcrFailed}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[10px] text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            {lang === 'zh' ? '解析中...' : 'Processing...'}
                          </span>
                        )}
                        {(file.processing_status === 'done' || file.processing_status === 'failed') && (
                          <button
                            type="button"
                            onClick={() => handleReprocess(file.file_id)}
                            disabled={reprocessingIds.has(file.file_id)}
                            className="inline-flex items-center gap-1 text-[10px] text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded hover:bg-orange-100 transition-colors disabled:opacity-50"
                          >
                            <RefreshCw className={`w-3 h-3 ${reprocessingIds.has(file.file_id) ? 'animate-spin' : ''}`} />
                            {reprocessingIds.has(file.file_id)
                              ? (lang === 'zh' ? 'OCR解析中...' : 'Processing...')
                              : (lang === 'zh' ? '重新解析' : 'Reprocess')}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => onPreview?.(file)}
                        className="p-1.5 rounded text-brand-400 hover:bg-blue-50 hover:text-blue-600 transition-colors"
                        title={lang === 'zh' ? '预览' : 'Preview'}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <a
                        href={`/matters/${matterId}/files/${file.file_id}/download`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 rounded text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Download className="w-4 h-4" />
                      </a>
                      <button
                        type="button"
                        onClick={() => handleDelete(file.file_id, file.filename)}
                        className="p-1.5 rounded text-brand-400 hover:bg-red-50 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── 文件预览弹窗 ────────────────────────────────────────────────────────────
function FilePreviewModal({
  matterId,
  file,
  lang,
  onClose,
}: {
  matterId: string
  file: MatterFileItem
  lang: string
  onClose: () => void
}) {
  const [text, setText] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [blobUrl, setBlobUrl] = useState<string | null>(null)

  const isImage = /\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(file.filename)
  const isPdf = /\.pdf$/i.test(file.filename)
  const downloadUrl = `/matters/${matterId}/files/${file.file_id}/download`

  useEffect(() => {
    let objectUrl: string | null = null

    if (isImage || isPdf) {
      // Fetch binary via JS fetch (carries auth header from interceptor)
      fetch(`/matters/${matterId}/files/${file.file_id}/view`)
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`)
          return r.blob()
        })
        .then((blob) => {
          objectUrl = URL.createObjectURL(blob)
          setBlobUrl(objectUrl)
        })
        .catch(() => setBlobUrl(null))
        .finally(() => setLoading(false))
    } else {
      setLoading(true)
      fetch(`/matters/${matterId}/files/${file.file_id}/preview`)
        .then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status}`)
          return r.json()
        })
        .then((data: { extracted_text?: string }) => {
          setText(data.extracted_text || null)
        })
        .catch(() => setText(null))
        .finally(() => setLoading(false))
    }

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [matterId, file.file_id, isImage, isPdf])

  const ext = file.filename.split('.').pop()?.toUpperCase() ?? ''

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] mx-4 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-brand-200 flex-shrink-0">
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-bold text-brand-800 truncate">{file.filename}</h3>
            <div className="flex items-center gap-3 mt-1 text-xs text-brand-400">
              <span>{ext} · {file.size_bytes < 1024 * 1024 ? `${(file.size_bytes / 1024).toFixed(1)} KB` : `${(file.size_bytes / (1024 * 1024)).toFixed(1)} MB`}</span>
              {file.text_chars > 0 && (
                <span className="text-green-600">{lang === 'zh' ? '已解析' : 'Parsed'} · {file.text_chars.toLocaleString()} {lang === 'zh' ? '字符' : 'chars'}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                fetch(downloadUrl)
                  .then((r) => r.blob())
                  .then((blob) => {
                    const url = URL.createObjectURL(blob)
                    const a = document.createElement('a')
                    a.href = url
                    a.download = file.filename
                    a.click()
                    URL.revokeObjectURL(url)
                  })
              }}
              className="p-2 rounded-lg text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors"
              title={lang === 'zh' ? '下载' : 'Download'}
            >
              <Download className="w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg text-brand-400 hover:bg-brand-100 hover:text-brand-600 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-sm text-brand-400">
              {lang === 'zh' ? '加载中...' : 'Loading...'}
            </div>
          ) : isPdf && blobUrl ? (
            <iframe
              src={blobUrl}
              className="w-full h-full min-h-[70vh]"
              title={file.filename}
            />
          ) : isImage && blobUrl ? (
            <div className="flex items-center justify-center p-6 bg-brand-50">
              <img
                src={blobUrl}
                alt={file.filename}
                className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-sm"
              />
            </div>
          ) : text ? (
            <div className="p-6">
              <div className="text-sm text-brand-700 leading-relaxed whitespace-pre-wrap font-mono bg-brand-50 rounded-xl p-5">
                {text}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <FileText className="w-12 h-12 text-brand-200 mb-3" />
              <div className="text-sm text-brand-500 mb-1">
                {lang === 'zh' ? '无法预览此文件格式' : 'Cannot preview this file format'}
              </div>
              <div className="text-xs text-brand-400 mb-4">
                {lang === 'zh' ? '请下载后使用对应应用打开' : 'Please download and open with appropriate app'}
              </div>
              <button
                type="button"
                onClick={() => {
                  fetch(downloadUrl)
                    .then((r) => r.blob())
                    .then((blob) => {
                      const url = URL.createObjectURL(blob)
                      const a = document.createElement('a')
                      a.href = url
                      a.download = file.filename
                      a.click()
                      URL.revokeObjectURL(url)
                    })
                }}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                {lang === 'zh' ? '下载文件' : 'Download File'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
